<template>
  <div class="enhanced-pdf-viewer">
    <!-- PDF 확대/축소 컨트롤 -->
<!--    <div class="pdf-controls">
    <div class="pdf-controls">
      <button @click="zoomOut" class="zoom-btn">-</button>
      <span class="zoom-level">{{ Math.round(pdfScale * 100) }}%</span>
      <button @click="zoomIn" class="zoom-btn">+</button>
      <button @click="resetZoom" class="reset-btn">초기화</button>
    </div>-->

    <div class="pdf-container" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd">
      <section class="main-section">
        <div id="pdfContent" ref="pdfContent"></div>
      </section>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

export default {
  name: 'EnhancedPdfViewer',
  props: {
    name: {
      type: String,
      default: '약관'
    },
    url: {
      type: String,
      required: true
    },
    initialScale: {
      type: Number,
      default: 1.0
    }
  },
  data () {
    return {
      pdfScale: this.initialScale,
      minScale: 0.5,
      maxScale: 3.0,
      scaleStep: 0.25,
      lastDistance: 0,
      touchStartScale: 1.0
    }
  },
  async mounted () {
    if (this.url) {
      await this.rawPdfContent()
      await this.resetZoom()
    }
  },
  watch: {
    url: {
      handler (newUrl) {
        if (newUrl) {
          this.rawPdfContent()
        }
      },
      immediate: true
    }
  },
  methods: {

    async resetZoom () {
      this.pdfScale = this.initialScale
      this.applyZoom()
    },

    // 터치 제스처 처리
    handleTouchStart (event) {
      if (event.touches.length === 2) {
        const touch1 = event.touches[0]
        const touch2 = event.touches[1]
        this.lastDistance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
        )
        this.touchStartScale = this.pdfScale
      }
    },

    async handleTouchMove (event) {
      if (event.touches.length === 2) {
        event.preventDefault() // 웹뷰의 기본 줌 동작 방지
        const touch1 = event.touches[0]
        const touch2 = event.touches[1]
        const distance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
        )

        if (this.lastDistance > 0) {
          const scale = distance / this.lastDistance
          const newScale = this.touchStartScale * scale

          if (newScale >= this.minScale && newScale <= this.maxScale) {
            this.pdfScale = newScale
            this.applyZoom()
          }
        }
      }
    },

    handleTouchEnd () {
      this.lastDistance = 0
    },

    // PDF 렌더링 (고정 스케일로 한 번만 렌더링)
    async rawPdfContent () {
      if (!this.url) return

      try {
        const pdfDoc = await pdfjsLib.getDocument(this.url).promise
        const pdfSize = pdfDoc.numPages
        const pdfContent = this.$refs.pdfContent
        pdfContent.innerHTML = '' // 기존 내용 초기화

        for (let i = 1; i <= pdfSize; i++) {
          const page = await pdfDoc.getPage(i)
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')

          // 고해상도 렌더링을 위해 2.0 스케일 사용
          const viewport = page.getViewport({ scale: 3.0 })

          canvas.width = viewport.width
          canvas.height = viewport.height
          canvas.style.maxWidth = '100%'
          canvas.style.height = 'auto'
          canvas.style.display = 'block'
          canvas.style.imageRendering = 'crisp-edges' // 선명한 렌더링

          await page.render({ canvasContext: context, viewport }).promise

          pdfContent.appendChild(canvas)
        }

        // CSS transform으로 줌 적용
        this.applyZoom()
        pdfContent.scrollTop = 20
      } catch (error) {
        console.error('PDF 렌더링 실패:', error)
        this.$emit('error', error)
      }
    },

    // CSS transform으로 줌 적용
    applyZoom () {
      const pdfContent = this.$refs.pdfContent
      if (pdfContent) {
        pdfContent.style.transform = `scale(${this.pdfScale})`
        pdfContent.style.transformOrigin = 'top left' // 중앙에서 왼쪽으로 변경
        pdfContent.style.transition = 'transform 0.2s ease-out'

        // 줌 레벨에 따라 컨테이너 너비 조정
        const container = this.$refs.pdfContent.parentElement
        if (container && this.pdfScale > 1.0) {
          container.style.width = `${this.pdfScale * 100}%`
          container.style.overflowX = 'auto'
          // 왼쪽 여백 추가로 스크롤 가능하게
          container.style.paddingLeft = '20px'
        } else if (container) {
          container.style.width = '100%'
          container.style.overflowX = 'hidden'
          container.style.paddingLeft = '20px'
        }
      }
    }
  }
}
</script>

<style scoped>
.enhanced-pdf-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
}

.pdf-container {
  flex: 1;
  touch-action: manipulation;
  display: flex;
  justify-content: center;
  background: #f5f5f5;
}

.main-section {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  overflow-y: auto;
  overflow-x: auto;
  height: calc(100vh - 120px);
  padding: 20px;
  box-sizing: border-box;
  transition: width 0.2s ease-out;
}

#pdfContent {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  min-width: 100%;
  transform-origin: top left;
}

#pdfContent canvas {
  max-width: 100%;
  height: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  background: white;
  image-rendering: -moz-crisp-edges; /* Firefox */
  image-rendering: crisp-edges; /* 표준 */
  -webkit-font-smoothing: antialiased; /* 폰트 안티앨리어싱 */
  -moz-osx-font-smoothing: grayscale; /* macOS 폰트 렌더링 */
}

@media (max-width: 768px) {
  .main-section {
    max-width: 100%;
    padding: 10px;
    height: calc(100vh - 100px);
  }

  #pdfContent canvas {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
}
</style>
